import React, { useEffect, useRef, useCallback } from 'react';
import { useReservationForm } from '../../hooks/useReservationForm';
import {
    ModalOverlay,
    ModalContainer,
    ModalCloseButton,
    ModalHeader,
    ProductSummary,
    ProductThumb,
    ProductDetails,
    ReservationForm,
    FormGroup,
    FormRow,
    InputLabel,
    Select,
    ErrorMessage,
    SubmitButton,
    StatusMessage,
    SuccessContent,
    SuccessIcon,
    CheckboxGroup,
    CheckboxLabel,
    ShippingWarning
} from './ReservationModal.styled';

const ReservationModal = ({ product, isOpen, onClose }) => {
    const {
        formData,
        handleChange,
        handleSubmit,
        isSubmitting,
        submitStatus,
        errors,
        resetForm,
        shippingOptions
    } = useReservationForm(product);

    const modalRef = useRef(null);
    const closeButtonRef = useRef(null);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            closeButtonRef.current?.focus();
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [handleClose]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (submitStatus === 'success') {
        return (
            <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
                <ModalContainer ref={modalRef} onClick={e => e.stopPropagation()}>
                    <ModalCloseButton ref={closeButtonRef} onClick={handleClose}>
                        &times;
                    </ModalCloseButton>
                    <SuccessContent>
                        <SuccessIcon>&#10003;</SuccessIcon>
                        <h2>Reserva Enviada</h2>
                        <p>Hemos recibido tu solicitud para reservar:</p>
                        <strong>{product.name}</strong>
                        <p className="price">{product.price.toFixed(2)} EUR</p>
                        <p>Te contactaremos pronto para confirmar los detalles.</p>
                        <SubmitButton onClick={handleClose}>Cerrar</SubmitButton>
                    </SuccessContent>
                </ModalContainer>
            </ModalOverlay>
        );
    }

    return (
        <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
            <ModalContainer
                ref={modalRef}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Formulario de reserva"
            >
                <ModalCloseButton
                    ref={closeButtonRef}
                    onClick={handleClose}
                    aria-label="Cerrar modal"
                >
                    &times;
                </ModalCloseButton>

                <ModalHeader>Reservar Producto</ModalHeader>

                <ProductSummary>
                    <ProductThumb
                        src={`/img/${product.images[0]}`}
                        alt={product.name}
                    />
                    <ProductDetails>
                        <h3>{product.name}</h3>
                        <span className="price">{product.price.toFixed(2)} EUR</span>
                    </ProductDetails>
                </ProductSummary>

                <ReservationForm onSubmit={handleSubmit}>
                    <FormGroup>
                        <InputLabel htmlFor="name">Nombre completo *</InputLabel>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            placeholder="Tu nombre completo"
                            value={formData.name}
                            onChange={handleChange}
                            className={errors.name ? 'error' : ''}
                            disabled={isSubmitting}
                        />
                        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                    </FormGroup>

                    <FormRow>
                        <FormGroup>
                            <InputLabel htmlFor="email">Correo electrónico *</InputLabel>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="tu.correo@ejemplo.com"
                                value={formData.email}
                                onChange={handleChange}
                                className={errors.email ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                        </FormGroup>

                        <FormGroup>
                            <InputLabel htmlFor="phone">Teléfono *</InputLabel>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                placeholder="+34 XXX XXX XXX"
                                value={formData.phone}
                                onChange={handleChange}
                                className={errors.phone ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <InputLabel htmlFor="country">País de envío *</InputLabel>
                            {product?.onlyCanary ? (
                                <Select
                                    id="country"
                                    name="country"
                                    value="ES-GRANCANARIA"
                                    disabled
                                >
                                    <option value="ES-GRANCANARIA">
                                        {shippingOptions?.granCanariaOnly?.label}
                                    </option>
                                </Select>
                            ) : (
                                <Select
                                    id="country"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    className={errors.country ? 'error' : ''}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Seleccionar país...</option>
                                    {shippingOptions?.countries?.map(c => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </Select>
                            )}
                            {errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
                        </FormGroup>

                        <FormGroup>
                            <InputLabel htmlFor="postalCode">Código Postal *</InputLabel>
                            <input
                                type="text"
                                id="postalCode"
                                name="postalCode"
                                placeholder="Ej: 35001"
                                value={formData.postalCode}
                                onChange={handleChange}
                                className={errors.postalCode ? 'error' : ''}
                                disabled={isSubmitting}
                                maxLength={10}
                            />
                            {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
                        </FormGroup>
                    </FormRow>

                    {product?.onlyCanary && (
                        <ShippingWarning>
                            Este producto solo puede enviarse a Gran Canaria debido a su delicadeza.
                        </ShippingWarning>
                    )}

                    <CheckboxGroup>
                        <CheckboxLabel>
                            <input
                                type="checkbox"
                                name="privacyAccepted"
                                checked={formData.privacyAccepted}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                            <span>
                                He leído y acepto la{' '}
                                <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer">
                                    Política de Privacidad
                                </a>
                            </span>
                        </CheckboxLabel>
                        {errors.privacy && <ErrorMessage>{errors.privacy}</ErrorMessage>}
                    </CheckboxGroup>

                    <CheckboxGroup>
                        <CheckboxLabel>
                            <input
                                type="checkbox"
                                name="storyConsent"
                                checked={formData.storyConsent}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                            <span>
                                (Opcional) Autorizo a Duo a Color a compartir parte de mi historia como inspiración para su comunidad
                            </span>
                        </CheckboxLabel>
                    </CheckboxGroup>

                    <SubmitButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Enviando...' : 'Confirmar Reserva'}
                    </SubmitButton>

                    {submitStatus === 'error' && (
                        <StatusMessage type="error">
                            Error al enviar la reserva. Por favor, inténtalo de nuevo.
                        </StatusMessage>
                    )}

                    {submitStatus === 'rate_limit' && (
                        <StatusMessage type="error">
                            Has alcanzado el límite de reservas. Inténtalo más tarde.
                        </StatusMessage>
                    )}

                    {submitStatus === 'config_error' && (
                        <StatusMessage type="error">
                            Error de configuración. Contacta con nosotros directamente.
                        </StatusMessage>
                    )}

                    {submitStatus === 'already_reserved' && (
                        <StatusMessage type="error">
                            Este producto acaba de ser reservado por otro usuario.
                        </StatusMessage>
                    )}

                    {submitStatus === 'product_sold' && (
                        <StatusMessage type="error">
                            Este producto ya ha sido vendido.
                        </StatusMessage>
                    )}
                </ReservationForm>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default ReservationModal;
